import { JobStatus, Job, SupabaseJob, formatDateDisplay, formatDateTimeDisplay, formatSalary, dbFormatToStatus, addDays, parseDateFromISO, formatDateForDb, formatDateTimeForDb } from '@applicator/shared'

import { UserRules } from './types'

// Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
export const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'N/A'
  return formatDateDisplay(dateStr) === 'Not set' ? 'N/A' : formatDateDisplay(dateStr)
}

export const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return 'N/A'
  return formatDateTimeDisplay(dateStr) === 'Not set' ? 'N/A' : formatDateTimeDisplay(dateStr)
}

// Helper to capitalize first letter
const capitalizeStatus = (status: string): JobStatus => {
  return dbFormatToStatus(status)
}

// Helper function to compute effective date (same logic as JobDateChips)
export const computeEffectiveDate = (
  mode: string | null | undefined,
  manualValue: string | null | undefined,
  autoValue: string | null | undefined,
  field: 'follow_up' | 'deadline' | 'date_applied',
  sbJob: SupabaseJob,
  userRules: UserRules
): string | null => {
  if (mode === 'manual' && manualValue) {
    return manualValue
  }
  if (mode === 'cleared') {
    return null
  }
  // Auto mode: compute based on rules (same logic as JobDateChips)
  if (mode === 'auto' || !mode) {
    if (field === 'follow_up') {
      // Follow Up = applied_at + follow_up_days_after_applied
      const appliedDate = parseDateFromISO(sbJob.applied_at)
      if (appliedDate) {
        const days = userRules.follow_up_days_after_applied ?? 7
        const computedDate = addDays(appliedDate, days)
        // Convert Date to ISO string for consistency
        return computedDate.toISOString()
      }
      return null
    } else if (field === 'deadline') {
      // Deadline = saved_at + deadline_days_after_saved
      const savedDate = parseDateFromISO(sbJob.saved_at)
      if (savedDate) {
        const days = userRules.deadline_days_after_saved ?? 14
        const computedDate = addDays(savedDate, days)
        // Convert Date to ISO string for consistency
        return computedDate.toISOString()
      }
      return null
    } else if (field === 'date_applied') {
      // Date Applied: use the stored value (no auto-calculation)
      return autoValue || null
    }
  }
  // Fallback: use the main field value
  return autoValue || null
}

// Convert Supabase job to UI format
export const mapSupabaseToJob = (sbJob: SupabaseJob, userRules: UserRules): Job => {
  // Compute effective dates using the same logic as JobDateChips
  const effectiveDateApplied = computeEffectiveDate(
    sbJob.date_applied_mode,
    sbJob.date_applied_manual,
    sbJob.applied_at,
    'date_applied',
    sbJob,
    userRules
  )
  
  const effectiveDeadline = computeEffectiveDate(
    sbJob.deadline_mode,
    sbJob.deadline_manual,
    sbJob.deadline_at,
    'deadline',
    sbJob,
    userRules
  )
  
  const effectiveFollowUp = computeEffectiveDate(
    sbJob.follow_up_mode,
    sbJob.follow_up_manual,
    sbJob.follow_up_at,
    'follow_up',
    sbJob,
    userRules
  )

  return {
    id: sbJob.id,
    jobNo: sbJob.job_no || undefined,
    position: sbJob.title || '',
    company: sbJob.company || '',
    salary: formatSalary(sbJob.salary_max, sbJob.salary_currency),
    location: sbJob.location || 'N/A',
    status: capitalizeStatus(sbJob.status || 'bookmarked'),
    dateSaved: formatDateTime(sbJob.saved_at), // saved_at is always auto, no manual mode
    deadline: effectiveDeadline ? formatDateTime(effectiveDeadline) : 'N/A',
    dateApplied: effectiveDateApplied ? formatDateTime(effectiveDateApplied) : 'N/A',
    followUp: effectiveFollowUp ? formatDate(effectiveFollowUp) : 'Add date',
    excitement: sbJob.excitement || 0,
  }
}

// Форматирование числа с разделителями тысяч через пробел
const formatNumberWithSpaces = (value: string): string => {
  // Убираем все нецифровые символы
  const numericValue = value.replace(/\s/g, '').replace(/\D/g, '')
  if (!numericValue) return ''
  
  // Форматируем с пробелами каждые 3 цифры справа налево
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

// Форматирование зарплаты для отображения (из формата $100000 в $100 000)
export const formatSalaryForDisplay = (salary: string): string => {
  if (!salary || salary === '$0') return salary
  // Извлекаем число из строки типа "$100000"
  const numericValue = salary.replace(/\D/g, '')
  if (!numericValue) return salary
  // Форматируем с пробелами
  const formatted = formatNumberWithSpaces(numericValue)
  return `$${formatted}`
}

// Убираем форматирование (пробелы) для сохранения
export const unformatNumber = (value: string): string => {
  return value.replace(/\s/g, '').replace(/\D/g, '')
}

// Get status display text
export const getStatusDisplay = (status: JobStatus): string => {
  if (status === 'No Response') {
    return 'No Response 👻'
  }
  return status
}

// Преобразование даты из DD/MM/YYYY или DD/MM/YYYY, HH:MM в формат для Supabase
export const convertDateToSupabaseFormat = (dateStr: string, isTimestamp: boolean): string | null => {
  if (!dateStr || dateStr === 'N/A' || dateStr === 'Add date' || dateStr === 'Not set') return null
  
  // Парсим формат DD/MM/YYYY или DD/MM/YYYY, HH:MM
  let datePart = dateStr
  let timePart = null
  
  if (dateStr.includes(',')) {
    // Формат с временем: "DD/MM/YYYY, HH:MM"
    const parts = dateStr.split(',')
    datePart = parts[0].trim()
    timePart = parts[1]?.trim() || null
  }
  
  // Парсим дату DD/MM/YYYY
  const dateParts = datePart.split('/')
  if (dateParts.length !== 3) return null
  
  const day = parseInt(dateParts[0])
  const month = parseInt(dateParts[1]) - 1 // месяцы в JS начинаются с 0
  const year = parseInt(dateParts[2])
  
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null
  
  const date = new Date(year, month, day)
  
  // Если есть время, парсим его
  if (isTimestamp && timePart) {
    const timeParts = timePart.split(':')
    if (timeParts.length === 2) {
      const hours = parseInt(timeParts[0])
      const minutes = parseInt(timeParts[1])
      if (!isNaN(hours) && !isNaN(minutes)) {
        date.setHours(hours, minutes, 0, 0)
      }
    }
  } else if (isTimestamp) {
    // Для TIMESTAMPTZ без времени используем текущее время
    const now = new Date()
    date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds())
  } else {
    // Для DATE используем полночь
    date.setHours(0, 0, 0, 0)
  }
  
  // Используем функции из shared пакета для форматирования
  if (isTimestamp) {
    return formatDateTimeForDb(date)
  } else {
    return formatDateForDb(date)
  }
}
